// File: packages/components/src/agent/SkillsAndStatsStrip.tsx (Refactored)

import React from 'react';
import { Badge, StatCard, Tooltip } from '@dopomogai/ui';
import { motion } from 'framer-motion';
import clsx from 'clsx';

// Type definitions remain the same
export interface Skill {
  label: string;
  featureId: string; // Now mandatory for interactivity
  tooltip?: string;
}
export interface Stat {
  label: string;
  value: string;
  icon?: any; // e.g., Lucide icon component
  tooltip?: string;
}

type Props = {
  skills: Skill[];
  stats: Stat[];
  selectedSkillIds: Set<string>; // The set of selected feature IDs from the calculator state
  onSkillClick: (featureId: string) => void; // The callback to toggle a feature
};

/**
 * Provides an at-a-glance summary of agent capabilities (skills) and
 * key metrics (stats). It's now a presentational component.
 */
export function SkillsAndStatsStrip({ skills, stats, selectedSkillIds, onSkillClick }: Props) {
  return (
    <div className="space-y-4">
      {/* Section 1: Interactive Skill Chips */}
      <div>
        <h3 className="text-sm font-bold text-slate-500 mb-2 uppercase tracking-wider">
          Core Skills
        </h3>
        <div className="flex flex-wrap gap-2">
          {skills.map((skill) => {
            const isSelected = selectedSkillIds.has(skill.featureId);

            const chip = (
              <motion.div key={skill.featureId} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Badge
                  text={skill.label}
                  accentColor={isSelected ? 'green' : 'gray'}
                  onClick={() => onSkillClick(skill.featureId)}
                  className="cursor-pointer"
                  aria-pressed={isSelected}
                />
              </motion.div>
            );

            return skill.tooltip ? (
              <Tooltip key={skill.featureId} text={skill.tooltip}>{chip}</Tooltip>
            ) : (
              chip
            );
          })}
        </div>
      </div>

      {/* Section 2: Stat Blocks */}
      {stats.length > 0 && (
        <div>
          <h3 className="text-sm font-bold text-slate-500 mb-2 uppercase tracking-wider">
            Key Metrics
          </h3>
          <div className="grid grid-cols-2 gap-3">
            {stats.slice(0, 4).map((stat, i) => (
              <StatCard
                key={i}
                title={stat.label}
                value={stat.value}
                icon={stat.icon}
                tooltipText={stat.tooltip}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}